/* eslint-disable react/require-default-props */
import React, { FC, useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import Model from "@tripian/model";
import {
  AccommondationCard,
  RouteResult,
  SvgIcons,
} from "@tripian/react";
import ICombinedState from "../../../redux/model/ICombinedState";
import useTrip from "../../../hooks/useTrip";
import useTimelineStep from "../../../hooks/useTimelineStep";
import useReaction from "../../../hooks/useReaction";
import useAlternative from "../../../hooks/useAlternative";
import useFocus from "../../../hooks/useFocus";
import useTranslate from "../../../hooks/useTranslate";
import TimelineSortableStepList from "./TimelineSortableStepList";
import type { DeletedStepSlot } from "./TimelineSortableStepList/TimelineSortableStepList";
import { TimelineSegmentGroup } from "../../../utils/timelineAdapters";
import { EDIT_TIMELINE_TRIP } from "../../../constants/ROUTER_PATH_TITLE";
import classes from "../../TripPlan/components/Itineraries/Itineraries.module.scss";

// localStorage key for deleted slots, scoped to tripHash
const DELETED_SLOTS_PREFIX = "timeline_deleted_slots_seg_";

interface ITimelineSegmentItineraries {
  segmentGroups: TimelineSegmentGroup[];
  plans?: Model.Plan[];
  alternativesStepId?: number;
  setAlternativesStepId: (stepId?: number) => void;
  customPoiModalShow: (segmentIndex: number) => void;
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  setShowCloneModal: (show: boolean) => void;
  onBookingClick: () => void;
  // Active segment for map display (first expanded segment)
  activeSegmentIndex: number | null;
  onSegmentToggle: (index: number | null) => void;
  // Segment delete handler
  onSegmentDelete: (segmentIndex: number) => void;
}

const getAccommendationStep = (
  accommendation: Model.Accommodation
): Model.Step => {
  const accommendationPoi: Model.Poi = {
    id: "",
    icon: "Homebase",
    name: accommendation.name || "Accommodation",
    description: accommendation.address || "",
    coordinate: accommendation.coordinate || { lat: 0, lng: 0 },
    web: accommendation.refID ?? "",
    address: accommendation.provider ?? "Google",
    image: {
      url: accommendation.imageUrl ?? "",
      imageOwner: { title: "", url: "" },
      width: null,
      height: null,
    },
    cityId: 0,
    gallery: [],
    price: 5,
    rating: 5,
    ratingCount: 0,
    phone: null,
    bookings: [],
    category: [],
    tags: [],
    mustTries: [],
    cuisines: null,
    attention: null,
    hours: null,
    closed: [],
    distance: null,
    status: true,
    safety: [],
    offers: [],
  };

  return {
    id: 0,
    poi: accommendationPoi,
    alternatives: [],
    order: -1,
    score: null,
    scoreDetails: [],
    times: { from: "", to: "" },
    warningMessage: [],
    stepType: "poi",
  };
};

// Calculate total walking distance for a segment
const calculateTotalDistance = (legs: RouteResult.ILeg[]): string => {
  if (!legs || legs.length === 0) return "";

  let totalMeters = 0;
  legs.forEach(leg => {
    // Extract numeric value from distance text (e.g., "1.2 km" -> 1200)
    const match = leg.distance.text.match(/^([\d.]+)\s*(km|m|mi|ft)?/i);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = (match[2] || "m").toLowerCase();
      if (unit === "km") {
        totalMeters += value * 1000;
      } else if (unit === "mi") {
        totalMeters += value * 1609.34;
      } else if (unit === "ft") {
        totalMeters += value * 0.3048;
      } else {
        totalMeters += value;
      }
    }
  });

  if (totalMeters >= 1000) {
    return `${(totalMeters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(totalMeters)} m`;
};

// Calculate total duration for a segment
const calculateTotalDuration = (legs: RouteResult.ILeg[]): string => {
  if (!legs || legs.length === 0) return "";

  let totalMinutes = 0;
  legs.forEach(leg => {
    // Extract numeric value from duration text (e.g., "15 mins" -> 15)
    const match = leg.duration.text.match(/(\d+)\s*(hour|hr|min|sec)?s?/gi);
    if (match) {
      match.forEach(m => {
        const parts = m.match(/(\d+)\s*(hour|hr|min|sec)?/i);
        if (parts) {
          const value = parseInt(parts[1], 10);
          const unit = (parts[2] || "min").toLowerCase();
          if (unit.startsWith("hour") || unit === "hr") {
            totalMinutes += value * 60;
          } else if (unit.startsWith("min")) {
            totalMinutes += value;
          }
        }
      });
    }
  });

  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  }
  return `${totalMinutes} min`;
};

const TimelineSegmentItineraries: FC<ITimelineSegmentItineraries> = ({
  segmentGroups,
  plans,
  alternativesStepId,
  setAlternativesStepId,
  customPoiModalShow,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  setShowCloneModal,
  onBookingClick,
  activeSegmentIndex,
  onSegmentToggle,
  onSegmentDelete,
}) => {
  const { tripReference } = useTrip();
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { timelineStepAdd, timelineStepReplace, timelineStepUpdateTimes, timelineStepDelete } = useTimelineStep();
  const { alternatives } = useAlternative();
  const { reactions, reactionAdd, reactionDelete, loadingReactionStep } = useReaction();
  const { focusStep, focusAlternative, focusLost } = useFocus();
  const { t } = useTranslate();

  const { hashParam } = useParams<{ hashParam: string; dayIndex: string }>();

  // Extract tripHash from hashParam (remove !s suffix if shared)
  const tripHash = useMemo(() => {
    if (!hashParam) return "";
    return hashParam.split("!")[0];
  }, [hashParam]);

  // Track which segments are collapsed
  // Use activeSegmentIndex from parent - collapsed means NOT the active segment
  const isSegmentCollapsed = useCallback((segmentIndex: number) => {
    return activeSegmentIndex !== segmentIndex;
  }, [activeSegmentIndex]);

  // Deleted slots state - keyed by segment index, persisted to localStorage per tripHash
  const [deletedSlotsBySegment, setDeletedSlotsBySegment] = useState<Record<number, DeletedStepSlot[]>>({});

  // Load deleted slots from localStorage on mount / tripHash change
  useEffect(() => {
    if (!tripHash) return;
    const key = `${DELETED_SLOTS_PREFIX}${tripHash}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        setDeletedSlotsBySegment(JSON.parse(stored));
      } catch {
        setDeletedSlotsBySegment({});
      }
    } else {
      setDeletedSlotsBySegment({});
    }
  }, [tripHash]);

  const { legs, readOnlyTrip } = useSelector((state: ICombinedState) => ({
    legs: state.gmap.legs,
    readOnlyTrip: state.trip.readOnly,
  }));

  const sharedTrip = useMemo(() => {
    if (!hashParam) return false;
    const params = hashParam.split("!");
    return params.length > 1 && hashParam.split("!")[1] === "s";
  }, [hashParam]);

  // Toggle segment - when a segment is clicked, it becomes active (expanded)
  // If clicked again, it collapses (no active segment)
  const toggleSegmentCollapse = (segmentIndex: number) => {
    if (activeSegmentIndex === segmentIndex) {
      // Clicking on already active segment - collapse it
      onSegmentToggle(null);
    } else {
      // Clicking on a different segment - make it active
      onSegmentToggle(segmentIndex);
    }
  };

  const memoizedThumbs = useCallback(
    (step?: Model.Step) => {
      let t = 0;
      const stepReaction = reactions?.find(
        userReaction =>
          userReaction.stepId === step?.id && userReaction.poiId === step.poi.id
      );
      if (stepReaction) {
        if (stepReaction.reaction === Model.REACTION.THUMBS_UP) t = 1;
        else if (stepReaction.reaction === Model.REACTION.THUMBS_DOWN) t = -1;
      }
      return t;
    },
    [reactions]
  );

  const memoizedThumbsClicked = useCallback(
    (step: Model.Step, thumbs: number) => {
      const userReactionRequest: Model.UserReactionRequest = {
        stepId: step.id,
        poiId: step.poi.id,
        reaction: thumbs === -1 ? Model.REACTION.THUMBS_DOWN : Model.REACTION.THUMBS_UP,
      };
      reactionAdd(userReactionRequest);
    },
    [reactionAdd]
  );

  const memoizedThumbsUndo = useCallback(
    (step: Model.Step) => {
      const stepUserReaction = reactions?.find(
        userReaction => userReaction.stepId === step.id
      );
      if (stepUserReaction) {
        reactionDelete(stepUserReaction.id, stepUserReaction.stepId || 0);
      }
    },
    [reactions, reactionDelete]
  );

  const alternativeDayNumbers = (alternativePois: Model.Poi[]): number[][] => {
    const getDayNumbers = (poiId: string): number[] => {
      if (poiId === "" || !plans) return [];
      const partOfDayNumbers: number[] = [];
      for (let i = 0; i < plans.length; i += 1) {
        const stepIndex = plans[i].steps.findIndex(step => step.poi.id === poiId);
        if (stepIndex > -1) partOfDayNumbers.push(i + 1);
      }
      return partOfDayNumbers;
    };
    return alternativePois.map(ap => getDayNumbers(ap.id));
  };

  const windowOpenBookARide = (leg: RouteResult.ILeg) => {
    const travel = leg.travel?.between;

    if (!travel?.pickup?.coordinate || !travel?.dropoff?.coordinate) {
      window.open("https://m.uber.com", "_blank");
      return;
    }

    const pickup = {
      latitude: travel.pickup.coordinate.lat,
      longitude: travel.pickup.coordinate.lng,
      addressLine1: travel.pickup.nickname,
      addressLine2: travel.pickup.formatted_address
    };

    const dropoff = {
      latitude: travel.dropoff.coordinate.lat,
      longitude: travel.dropoff.coordinate.lng,
      addressLine1: travel.dropoff.nickname,
      addressLine2: travel.dropoff.formatted_address
    };

    const uberClientId =
      window.tconfig.PROVIDERS.transportation.find(
        x => x.id === Model.PROVIDER_ID.UBER
      )?.clientId ?? "";

    const bookARideUrl =
      "https://m.uber.com/looking" +
      `?client_id=${uberClientId}` +
      `&pickup=${encodeURIComponent(JSON.stringify(pickup))}` +
      `&drop[0]=${encodeURIComponent(JSON.stringify(dropoff))}`;

    window.open(bookARideUrl, "_blank");
  };


  const cloneTrip = () => setShowCloneModal(true);

  const accommendationMemo = useMemo(() => {
    if (tripReference?.tripProfile.accommodation) {
      const { accommodation } = tripReference.tripProfile;
      return (
        <AccommondationCard
          accommodation={accommodation}
          clicked={() => focusStep(getAccommendationStep(accommodation))}
        />
      );
    }
    return null;
  }, [tripReference, focusStep]);

  const handleDeletedSlotsChange = useCallback(
    (segmentIndex: number, newSlots: DeletedStepSlot[]) => {
      setDeletedSlotsBySegment(prev => {
        const updated = { ...prev, [segmentIndex]: newSlots };
        // Clean up empty arrays
        if (newSlots.length === 0) {
          delete updated[segmentIndex];
        }
        // Persist to localStorage
        if (tripHash) {
          const key = `${DELETED_SLOTS_PREFIX}${tripHash}`;
          if (Object.keys(updated).length > 0) {
            localStorage.setItem(key, JSON.stringify(updated));
          } else {
            localStorage.removeItem(key);
          }
        }
        return updated;
      });
    },
    [tripHash]
  );

  // Format date range for segment header
  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.format("YYYY-MM-DD") === end.format("YYYY-MM-DD")) {
      // Same day
      return `${start.format("MMM DD")} • ${start.format("HH:mm")} - ${end.format("HH:mm")}`;
    } else {
      // Different days
      return `${start.format("MMM DD, HH:mm")} - ${end.format("MMM DD, HH:mm")}`;
    }
  };

  const renderSegmentContent = (
    segment: TimelineSegmentGroup,
    segmentLegs: RouteResult.ILeg[]
  ) => {
    // Check if segment is still generating
    const isGenerating = segment.plan?.generatedStatus === 0;

    // Booking segment (available = false)
    if (!segment.available) {
      // If smartRecommendation is false, show only reservation card (old behavior)
      if (!segment.smartRecommendation && (segment.plan?.steps?.length ?? 0) === 0) {
        return (
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SvgIcons.Calendar size="1rem" fill="#B45309" />
              <span className="text-amber-700 font-medium">Reservation</span>
            </div>
            {segment.description && (
              <p className="text-sm text-gray-600">{segment.description}</p>
            )}
          </div>
        );
      }

      // If smartRecommendation is true, show reservation card + steps below
      const sortedSteps = segment.steps;
      const deletedSlots = deletedSlotsBySegment[segment.segmentIndex] || [];
      const plan = segment.plan ? {
        id: parseInt(segment.plan.id, 10) || segment.segmentIndex + 1,
        date: moment(segment.startDate).format("YYYY-MM-DD"),
        startTime: moment(segment.startDate).format("HH:mm"),
        endTime: moment(segment.endDate).format("HH:mm"),
        generatedStatus: segment.plan.generatedStatus,
        steps: sortedSteps,
      } : null;

      return (
        <>
          {/* Reservation card */}
          {(segment.plan?.steps?.length ?? 0) === 0 && (
            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <SvgIcons.Calendar size="1rem" fill="#B45309" />
                <span className="text-amber-700 font-medium">Reservation</span>
              </div>
              <p className="text-sm text-gray-600">{segment.description}</p>
            </div>
          )}
          {/* Steps below reservation */}
          {sortedSteps && sortedSteps.length > 0 && (
            <TimelineSortableStepList
              steps={sortedSteps}
              alternatives={alternatives ?? []}
              alternativeDayNumbers={alternativeDayNumbers}
              alternativesStepId={alternativesStepId}
              showAlternativesChange={(stepId, show) => setAlternativesStepId(show ? stepId : undefined)}
              hideScore={sharedTrip && window.tconfig.WIDGET_THEME_1}
              hideStepsTime={sharedTrip && window.tconfig.WIDGET_THEME_1}
              hideFeatures={sharedTrip && window.tconfig.WIDGET_THEME_1}
              hideCuisine={sharedTrip && window.tconfig.WIDGET_THEME_1}
              isWidget={sharedTrip && window.tconfig.WIDGET_THEME_1}
              alternativePoiCardClicked={(stepId, alternativePoi) => focusAlternative(stepId, alternativePoi)}
              alternativeReplace={(stepId, alternativePoi) => {
                if (readOnlyTrip) cloneTrip();
                else {
                  focusLost();
                  // Null check to prevent error if alternativePoi is undefined
                  if (alternativePoi && alternativePoi.id) {
                    timelineStepReplace(stepId, alternativePoi.id);
                  }
                }
              }}
              memoizedThumbs={memoizedThumbs}
              thumbsClicked={(step, like) => {
                if (readOnlyTrip) cloneTrip();
                else memoizedThumbsClicked(step, like);
              }}
              timesClicked={(stepId, from, to) => {
                if (readOnlyTrip) cloneTrip();
                else {
                  focusLost();
                  timelineStepUpdateTimes(stepId, from, to);
                }
              }}
              loadingReactionStep={loadingReactionStep}
              userReactionUndo={step => {
                if (readOnlyTrip) cloneTrip();
                else {
                  memoizedThumbsUndo(step);
                  setAlternativesStepId(undefined);
                }
              }}
              userReactionRemoveStep={(stepId, _step) => {
                if (readOnlyTrip) {
                  cloneTrip();
                  return Promise.reject("readonly");
                }
                return timelineStepDelete(stepId);
              }}
              focusStep={(step: Model.Step) => focusStep(step)}
              onBookClick={(step: Model.Step) => {
                focusStep(step);
                onBookingClick();
              }}
              onAddStepFromSlot={(poiId, timeFrom, timeTo) => {
                if (plan) {
                  timelineStepAdd(plan.id.toString(), poiId, timeFrom, timeTo);
                }
              }}
              legs={segmentLegs}
              legsOffset={0}
              bookaride={(leg) => windowOpenBookARide(leg)}
              gygTourIds={gygTourIds}
              bbTourIds={bbTourIds}
              viatorTourIds={viatorTourIds}
              toristyTourIds={toristyTourIds}
              toursLoading={toursLoading}
              deletedSlots={deletedSlots}
              onDeletedSlotsChange={(newSlots) => handleDeletedSlotsChange(segment.segmentIndex, newSlots)}
              t={t}
            />
          )}
        </>
      );
    }

    // Show loading state if segment is generating
    if (isGenerating) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-10 h-10 border-3 border-primary-color border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600 font-medium">Generating your itinerary...</p>
          <p className="text-gray-400 text-sm mt-1">This may take a few moments</p>
        </div>
      );
    }

    // Available segment with steps
    const sortedSteps = segment.steps;
    const deletedSlots = deletedSlotsBySegment[segment.segmentIndex] || [];

    if (!sortedSteps || sortedSteps.length === 0) {
      return (
        <div className="p-4 text-gray-500 text-sm text-center">
          {t("trips.myTrips.itinerary.error.emptyMesssage")}
        </div>
      );
    }

    // Get plan for this segment
    const plan = segment.plan ? {
      id: parseInt(segment.plan.id, 10) || segment.segmentIndex + 1,
      date: moment(segment.startDate).format("YYYY-MM-DD"),
      startTime: moment(segment.startDate).format("HH:mm"),
      endTime: moment(segment.endDate).format("HH:mm"),
      generatedStatus: segment.plan.generatedStatus,
      steps: sortedSteps,
    } : null;

    return (
      <>
        <TimelineSortableStepList
          steps={sortedSteps}
          alternatives={alternatives ?? []}
          alternativeDayNumbers={alternativeDayNumbers}
          alternativesStepId={alternativesStepId}
          showAlternativesChange={(stepId, show) => setAlternativesStepId(show ? stepId : undefined)}
          hideScore={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideStepsTime={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideFeatures={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideCuisine={sharedTrip && window.tconfig.WIDGET_THEME_1}
          isWidget={sharedTrip && window.tconfig.WIDGET_THEME_1}
          alternativePoiCardClicked={(stepId, alternativePoi) => focusAlternative(stepId, alternativePoi)}
          alternativeReplace={(stepId, alternativePoi) => {
            if (readOnlyTrip) cloneTrip();
            else {
              focusLost();
              // Null check to prevent error if alternativePoi is undefined
              if (alternativePoi && alternativePoi.id) {
                timelineStepReplace(stepId, alternativePoi.id);
              }
            }
          }}
          memoizedThumbs={memoizedThumbs}
          thumbsClicked={(step, like) => {
            if (readOnlyTrip) cloneTrip();
            else memoizedThumbsClicked(step, like);
          }}
          timesClicked={(stepId, from, to) => {
            if (readOnlyTrip) cloneTrip();
            else {
              focusLost();
              timelineStepUpdateTimes(stepId, from, to);
            }
          }}
          loadingReactionStep={loadingReactionStep}
          userReactionUndo={step => {
            if (readOnlyTrip) cloneTrip();
            else {
              memoizedThumbsUndo(step);
              setAlternativesStepId(undefined);
            }
          }}
          userReactionRemoveStep={(stepId, _step) => {
            if (readOnlyTrip) {
              cloneTrip();
              return Promise.reject("readonly");
            }
            return timelineStepDelete(stepId);
          }}
          focusStep={(step: Model.Step) => focusStep(step)}
          onBookClick={(step: Model.Step) => {
            focusStep(step);
            onBookingClick();
          }}
          onAddStepFromSlot={(poiId, timeFrom, timeTo) => {
            if (plan) {
              timelineStepAdd(plan.id.toString(), poiId, timeFrom, timeTo);
            }
          }}
          legs={segmentLegs}
          legsOffset={0}
          bookaride={(leg) => windowOpenBookARide(leg)}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          deletedSlots={deletedSlots}
          onDeletedSlotsChange={(newSlots) => handleDeletedSlotsChange(segment.segmentIndex, newSlots)}
          t={t}
        />
      </>
    );
  };

  return (
    <div className="h-full py-4">
      <div ref={scrollRef} className="w-full relative">
        {/* Header */}
        <div className="my-4">
          <div className="font-medium text-xl text-[#0B1215]">
            {t("trips.myTrips.itinerary.title")}
          </div>
        </div>

        {/* Accommodation */}
        {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && accommendationMemo}

        {/* All Segments with Collapse */}
        <div className={`${classes.stepList}`}>
          {segmentGroups.map((segment) => {
            const isCollapsed = isSegmentCollapsed(segment.segmentIndex);
            const stepsCount = segment.steps.length;
            // Check if segment is still generating (generatedStatus === 0)
            const isSegmentGenerating = segment.plan?.generatedStatus === 0;

            return (
              <div key={segment.segmentIndex} className="mb-4">
                {/* Collapse Header */}
                <div
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-xl cursor-pointer transition-colors ${segment.available
                    ? "bg-white border border-gray-200 hover:bg-gray-50"
                    : "bg-amber-50 border border-amber-200 hover:bg-amber-100"
                    }`}
                  onClick={() => toggleSegmentCollapse(segment.segmentIndex)}
                >
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    {/* Color indicator for segment route */}
                    <div
                      className="w-2 sm:w-3 h-10 sm:h-12 rounded-full shrink-0"
                      style={{ backgroundColor: segment.routeColor }}
                    />

                    {/* Segment Info */}
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 font-semibold text-sm sm:text-base text-gray-900">
                        <span className="truncate">{segment.title || (segment.available ? `Segment ${segment.segmentIndex + 1}` : "Reservation")}</span>
                        {/* Loading spinner for generating segments */}
                        {isSegmentGenerating && (
                          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-primary-color border-t-transparent rounded-full animate-spin" />
                            <span className="text-[10px] sm:text-xs font-normal text-primary-color hidden xs:inline">Generating...</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                        <span className="whitespace-nowrap">{formatDateRange(segment.startDate, segment.endDate)}</span>
                        {segment.cityName && (
                          <>
                            <span className="text-gray-300 hidden sm:inline">•</span>
                            <span className="hidden sm:inline">{segment.cityName}</span>
                          </>
                        )}
                        {(segment.available || segment.smartRecommendation) && stepsCount > 0 && !isSegmentGenerating && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="whitespace-nowrap">{stepsCount} {stepsCount === 1 ? "place" : "places"}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 mt-2 sm:mt-0 ml-4 sm:ml-0 shrink-0">
                    {/* Edit Button - only for available segments and not shared trips */}
                    {segment.available && !(sharedTrip && window.tconfig.WIDGET_THEME_1) && tripHash && (
                      <div
                        className="border border-solid border-neutral-300 rounded-full flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 cursor-pointer bg-white hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${EDIT_TIMELINE_TRIP.PATH}/${tripHash}/${segment.segmentIndex}`);
                        }}
                        title="Edit segment"
                      >
                        <SvgIcons.Edit size="0.75rem" fill="#6B7280" />
                      </div>
                    )}

                    {/* Add Button - only for available segments */}
                    {segment.available && !(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
                      <div
                        className="border border-solid border-neutral-300 rounded-full flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 cursor-pointer bg-primary-color hover:opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          customPoiModalShow(segment.segmentIndex);
                        }}
                      >
                        <SvgIcons.Add size="0.75rem" fill="#fff" />
                      </div>
                    )}

                    {/* Delete Button - only for non-shared trips */}
                    {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && !readOnlyTrip && (
                      <div
                        className="border border-solid border-neutral-300 rounded-full flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 cursor-pointer bg-white hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Are you sure you want to delete this segment?")) {
                            onSegmentDelete(segment.segmentIndex);
                          }
                        }}
                        title="Delete segment"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="sm:w-[14px] sm:h-[14px]"
                        >
                          <path
                            d="M3 6H5H21"
                            stroke="#EF4444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                            stroke="#EF4444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}

                    {/* Collapse Arrow */}
                    <div
                      className={`transform transition-transform duration-200 ${isCollapsed ? "rotate-180" : "rotate-0"
                        }`}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Collapse Content */}
                {!isCollapsed && (
                  <div className="mt-2">
                    {renderSegmentContent(segment, legs)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Segment Button */}
        {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && !readOnlyTrip && tripHash && (
          <div className="mt-4 mb-4">
            <button
              className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary-color hover:text-primary-color transition-colors flex items-center justify-center gap-2"
              onClick={() => navigate(`${EDIT_TIMELINE_TRIP.PATH}/${tripHash}`)}
            >
              <SvgIcons.Add size="1.25rem" fill="currentColor" />
              <span className="font-medium">Add New Segment</span>
            </button>
          </div>
        )}

        {segmentGroups.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            {t("trips.myTrips.itinerary.error.emptyMesssage")}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineSegmentItineraries;
